"use client";

import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const MONTHS = [
  "styczeń",
  "luty",
  "marzec",
  "kwiecień",
  "maj",
  "czerwiec",
  "lipiec",
  "sierpień",
  "wrzesień",
  "październik",
  "listopad",
  "grudzień",
];

// Czytelny podpis okresu odznaki. Zwraca "" gdy nie ma sensownego okresu (passa jest permanentna —
// jej opis i tak tłumaczy "X dni nauki pod rząd"), dzięki czemu BadgeMark nie renderuje podpisu.
// Nigdy nie pokazujemy surowych tokenów ("streak", "2026-wiosna").
export const formatBadgePeriod = (
  period: "session" | "month" | "streak" | "practice",
  periodKey: string,
  periodLabel?: string | null,
): string => {
  // Passa: brak okresu do podpisu.
  if (period === "streak") return "";
  // Próbny LEK: podpis to nazwa edycji albo rundy (periodKey to techniczne "mg-12"/"me-7").
  if (period === "practice") return periodLabel ?? "";
  // Sesja: czytelna nazwa ("Wiosna 2026") jeśli backend ją podał, inaczej nic (nie surowy klucz).
  if (period === "session") return periodLabel ?? "";
  // Miesiąc: "2026-06" -> "czerwiec 2026".
  const [year, month] = periodKey.split("-");
  const name = MONTHS[Number(month) - 1];
  return name ? `${name} ${year}` : "";
};

// Natywne proporcje grafiki odznaki (portret) — nie wymuszamy kwadratu, tylko skalujemy z zachowaniem
// tego aspect-ratio. Atrybuty width/height dają przeglądarce ratio (bez CLS); realny rozmiar z CSS.
const BADGE_NATIVE_WIDTH = 116;
const BADGE_NATIVE_HEIGHT = 128;

// Pojedyncza odznaka: grafika z CDN (iconUrl) + tooltip Radixa renderowany w portalu,
// dzięki czemu dymek wychodzi poza kontenery z overflow (np. tabela rankingu).
export const BadgeMark = ({
  iconUrl,
  title,
  description,
  subtitle,
  size = 28,
  className,
  sizeClass,
  boxClass,
}: {
  // pełny URL grafiki na CDN; null gdy brak linku — wtedy ikona nie jest renderowana
  iconUrl: string | null;
  title: string;
  // czytelne "za co" jest odznaka
  description?: string | null;
  // dodatkowy wiersz, np. okres zdobycia
  subtitle?: string | null;
  // docelowy rozmiar boxu w px (kwadrat) — grafika wpisuje się w niego z zachowaniem proporcji
  size?: number;
  className?: string;
  // responsywna WYSOKOŚĆ boxu przez Tailwind (np. "h-5 sm:h-8"); nadpisuje `size`.
  // Bez `boxClass` box jest kwadratowy (aspect-square).
  sizeClass?: string;
  // stała SZEROKOŚĆ kontenera (np. "w-5 sm:w-8"), gdy potrzebny inny footprint niż kwadrat
  boxClass?: string;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span
        className={cn(
          // Stały, jednakowy footprint dla wszystkich odznak — grafiki mają różne proporcje
          // (sześciokąty vs. koła), więc box wymusza rozmiar, a obrazek się w niego wpisuje.
          "relative inline-flex shrink-0 items-center justify-center",
          sizeClass,
          boxClass ?? (sizeClass && "aspect-square"),
          className,
        )}
        style={sizeClass ? undefined : { width: size, height: size }}
      >
        {iconUrl ? (
          <img
            src={iconUrl}
            width={BADGE_NATIVE_WIDTH}
            height={BADGE_NATIVE_HEIGHT}
            alt={title}
            className="h-full w-full object-contain"
          />
        ) : null}
      </span>
    </TooltipTrigger>
    {/* side="bottom" — dymek zawsze pod odznaką; Radix sam odbija go, gdy brakuje miejsca w viewporcie */}
    <TooltipContent
      side="bottom"
      sideOffset={8}
      className="max-w-[220px] bg-zinc-900 text-left leading-snug text-white shadow-lg"
    >
      <span className="block font-semibold">{title}</span>
      {description ? (
        <span className="mt-0.5 block text-white/80">{description}</span>
      ) : null}
      {subtitle ? (
        <span className="mt-0.5 block text-white/60">{subtitle}</span>
      ) : null}
    </TooltipContent>
  </Tooltip>
);
